import React, { useMemo, useRef, useState } from "react";
import countryList from "react-select-country-list";
import { useNavigate, Routes, Route } from "react-router-dom";
import UserSettingsModal from "../../components/settings/SettingsModal";
import useUserDetails from "../../utils/hooks/user/useUserDetails";
import useUserCredentials from "../../utils/hooks/user/useUserCredentials";
import { useStoreSelector } from "../../utils/hooks/hooks";
import { UserRole } from "../../utils/api/endpoints/user/credentials";
import NotificationSettings from "../../components/settings/NotificationSettings";

const BusinessProfile = () => {
  const navigate = useNavigate();
  const { user, setDetails } = useUserDetails();
  const { role } = useUserCredentials();
  const emailFromStore = useStoreSelector(state => state.user.credentials.userEmail);
  const [activeTab, setActiveTab] = useState("profile");
  const [isEditing, setIsEditing] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement | null>(null);
  const countries = useMemo(() => countryList().getData(), []);
  const [formData, setFormData] = useState({
    username: user?.username || "",
    displayName: user?.displayName || "",
    email: emailFromStore || "",
    jobTitle: "",
    phoneNumber: "",
    bio: user?.about || "",
    country: user?.country || ""
  });

  const getJobTitleFromRole = (r?: UserRole) => {
    switch (r) {
      case UserRole.ADMIN:
        return "Security Administrator";
      case UserRole.BUSINESS:
        return "Business Administrator";
      case UserRole.QA:
      case UserRole.SUB_ADMIN:
      case UserRole.ADMIN_MANAGER:
        return "Admin Manager";
      case UserRole.DEVELOPER:
        return "Security Engineer";
      case UserRole.BUSINESS_MANAGER:
        return "Program Manager";
      case UserRole.BUSINESS_ADMINISTRATOR:
        return "Organisation Admin";
      default:
        return "";
    }
  };

  React.useEffect(() => {
    setFormData(prev => ({
      ...prev,
      username: user?.username || "",
      displayName: user?.displayName || "",
      email: emailFromStore || "",
      jobTitle: getJobTitleFromRole(role),
      bio: user?.about || "",
      country: user?.country || ""
    }));
  }, [user?.username, user?.displayName, user?.about, emailFromStore, role]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveChanges = async () => {
    try {
      await setDetails({
        username: formData.username || user?.username || "",
        displayName: formData.displayName,
        about: formData.bio,
        country: formData.country
      });
    } finally {
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    // Reset form data
    setFormData({
      username: user?.username || "",
      displayName: user?.displayName || "",
      email: emailFromStore || "",
      jobTitle: getJobTitleFromRole(role),
      phoneNumber: "",
      bio: user?.about || "",
      country: user?.country || ""
    });
    setIsEditing(false);
  };

  const handleAvatarClick = () => {
    if (isEditing && avatarInputRef.current) {
      avatarInputRef.current.click();
    }
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await setDetails({
        username: formData.username || user?.username || "",
        profilePicture: file
      });
    } catch { }
  };

  const getRoleDisplayName = () => {
    switch (role) {
      case UserRole.ADMIN:
        return "Security Administrator";
      case UserRole.BUSINESS:
        return "Business Administrator";
      case UserRole.QA:
      case UserRole.SUB_ADMIN:
      case UserRole.ADMIN_MANAGER:
        return "Admin Manager";
      case UserRole.DEVELOPER:
        return "Security Engineer";
      case UserRole.BUSINESS_MANAGER:
        return "Program Manager";
      case UserRole.BUSINESS_ADMINISTRATOR:
        return "Organisation Admin";
      default:
        return "Administrator";
    }
  };

  return (
    <>
      <div className="bg-gray-100 min-h-screen p-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 600, fontSize: "24px" }}>Profile & Organization Management</h1>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Profile Card & Navigation */}
          <div className="lg:col-span-1 space-y-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            {/* Profile Card Container */}
            <div className="bg-white rounded-lg p-6 shadow-sm w-[95%] mx-auto relative">
              {/* Profile Info */}
              <div className="text-center">
                <div className="mx-auto mb-4 relative w-24 h-24">
                  <img
                    src={user?.profilePicture || "/favicon.ico"}
                    alt="Profile"
                    className={`w-24 h-24 rounded-full object-cover border-4 border-white shadow-sm mx-auto ${isEditing ? "cursor-pointer" : ""
                      }`}
                    onClick={handleAvatarClick}
                  />
                  {isEditing && (
                    <button
                      type="button"
                      aria-label="Change profile picture"
                      onClick={handleAvatarClick}
                      className="absolute inset-0 rounded-full bg-black/35 flex items-center justify-center text-white"
                    >
                      <span className="w-24 h-24 rounded-full bg-white/70 flex items-center justify-center">
                        <svg
                          className="w-5 h-5 text-black/70"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M12 20h9" />
                          <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                        </svg>
                      </span>
                    </button>
                  )}
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    className="hidden"
                    onChange={handleAvatarChange}
                  />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-1">{user?.displayName || user?.username || "Alex Johnson"}</h2>
                <p className="text-gray-600 mb-3">{getRoleDisplayName()}</p>
                <span className="inline-block bg-[#010D2C] text-white text-xs px-2.5 py-0.5 rounded-full">
                  Active
                </span>
                <div className="border-b border-gray-200 mt-4"></div>
              </div>

              {/* Profile Navigation */}
              <div className="space-y-2 mt-6">
                <button
                  onClick={() => setActiveTab("profile")}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${activeTab === "profile"
                    ? "bg-[#58CC02]/[0.17] text-[#58CC02]"
                    : "text-gray-700 hover:bg-gray-50"
                    }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Profile Information
                </button>
                {(role === UserRole.BUSINESS || role === UserRole.ADMIN) && (
                  <button
                    onClick={() => navigate("/dashboard/settings/team")}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${"text-gray-700 hover:bg-gray-50"
                      }`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    Tenant Management
                  </button>
                )}
                <button
                  onClick={() => navigate("/dashboard/settings/security")}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${"text-gray-700 hover:bg-gray-50"
                    }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Security Settings
                </button>
              </div>
            </div>

            {/* Account Details Container */}
            <div className="bg-white rounded-lg p-6 shadow-sm w-[95%] mx-auto">
              <h3 className="text-xl font-semibold text-[#010D2C] mb-4">Account Details</h3>
              <div className="space-y-6">
                <div>
                  <div className="text-gray-500 font-medium">Email:</div>
                  <div className="text-gray-900">{emailFromStore || "-"}</div>
                </div>
                <div>
                  <div className="text-gray-500 font-medium">2FA:</div>
                  <div className="text-gray-900">{user?.otpEnabled ? "Enabled" : "Disabled"}</div>
                </div>
                <div>
                  <div className="text-gray-500 font-medium">Location:</div>
                  <div className="text-gray-900">{user?.country || "Location not specified"}</div>
                </div>
                <div className="pt-4 border-t border-gray-100">
                  <div className="text-gray-500 font-medium mb-2">Notifications:</div>
                  <button 
                    onClick={() => navigate("/dashboard/settings/notifications")}
                    className="text-blue-600 text-sm hover:underline"
                  >
                    Manage Notification Settings →
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Profile Information Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Profile Information</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-8">
                {/* Username */}
                <div>
                  <label htmlFor="username" className="block text-sm font-medium text-gray-600 mb-2">
                    Username
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      id="username"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  ) : (
                    <div className="mt-1 ml-1 text-gray-900 text-lg">{formData.username || "-"}</div>
                  )}
                </div>

                {/* Display Name */}
                <div>
                  <label htmlFor="displayName" className="block text-sm font-medium text-gray-600 mb-2">
                    Display Name
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      id="displayName"
                      name="displayName"
                      value={formData.displayName}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  ) : (
                    <div className="mt-1 ml-1 text-gray-900 text-lg">{formData.displayName || "-"}</div>
                  )}
                </div>

                {/* Location */}
                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-gray-600 mb-2">
                    Location
                  </label>
                  {isEditing ? (
                    <select
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select location...</option>
                      {countries.map((c: { label: string }) => (
                        <option key={c.label} value={c.label}>{c.label}</option>
                      ))}
                    </select>
                  ) : (
                    <div className="mt-1 ml-1 text-gray-900 text-lg">{formData.country || "-"}</div>
                  )}
                </div>

                {/* Job Title (read-only) */}
                <div>
                  <label htmlFor="jobTitle" className="block text-sm font-medium text-gray-600 mb-2">
                    Job Title
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      id="jobTitle"
                      name="jobTitle"
                      value={formData.jobTitle}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700"
                    />
                  ) : (
                    <div className="mt-1 ml-1 text-gray-900 text-lg">{formData.jobTitle || "-"}</div>
                  )}
                </div>
              </div>

              {/* Bio */}
              <div className="mt-6">
                <label htmlFor="bio" className="block text-sm font-medium text-gray-600 mb-2">
                  Bio
                </label>
                {isEditing ? (
                  <textarea
                    id="bio"
                    name="bio"
                    rows={4}
                    value={formData.bio}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                ) : (
                  <div className="text-gray-900 leading-relaxed bg-gray-50 border border-gray-200 rounded-md p-3 ml-1">{formData.bio || "-"}</div>
                )}
              </div>

              {/* Action Buttons */}
              {isEditing ? (
                <div className="flex justify-center gap-3 mt-8">
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveChanges}
                    className="px-4 py-2 bg-[#010D2C] text-white rounded-md hover:bg-[#010D2C]/90 transition-colors"
                  >
                    Save Changes
                  </button>
                </div>
              ) : (
                <div className="flex justify-center mt-8">
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-6 py-2.5 bg-[#010D2C] text-white rounded-lg hover:bg-[#010D2C]/90 transition-colors shadow-sm"
                  >
                    Edit
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Settings modal route removed in favor of page-based settings */}
    </>
  );
};

export default BusinessProfile;
